import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import FeedbackPopup from '../components/FeedbackPopup';
import { SEEDED_WAITLIST_COUNT, BACKEND_URL } from '../constants';
import { 
  Shield, 
  CreditCard, 
  Bell, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  DollarSign,
  Zap,
  Lock,
  Eye,
  TrendingUp,
  Activity,
  Target,
  BarChart3
} from 'lucide-react';
import '../styles/LandingPage.css';
import '../styles/CreditScorePage.css';

const CreditScorePage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [priorityProblem, setPriorityProblem] = useState('');
  const [otherProblem, setOtherProblem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(SEEDED_WAITLIST_COUNT);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Fetch waitlist count
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waitlist/count`);
        const data = await response.json();
        setWaitlistCount(SEEDED_WAITLIST_COUNT + data.count);
      } catch (error) {
        // Keep default value
      }
    };
    
    fetchWaitlistCount();
  }, []);

  // Handle demo click - USING CORRECT FIGMA URL FROM MAIN PAGE
  const handleDemoClick = useCallback((url) => {
    localStorage.setItem('demoOpened', 'true');
    
    if (window.posthog) {
      window.posthog.capture('demo_clicked_credit_score', {
        source_page: '/credit-score'
      });
    }
    
    window.open(url, '_blank');
  }, []);

  // Check for demo feedback
  useEffect(() => {
    let timeoutId;
    let intervalId;
    
    const showPopupIfNeeded = (source = 'unknown') => {
      const demoOpened = localStorage.getItem('demoOpened');
      
      if (demoOpened === 'true' && !showFeedbackPopup) {
        if (timeoutId) clearTimeout(timeoutId);
        
        timeoutId = setTimeout(() => {
          setShowFeedbackPopup(true);
          
          if (window.posthog) {
            window.posthog.capture('demo_feedback_popup_shown', {
              trigger_source: source,
              source_page: '/credit-score'
            });
          }
          
          localStorage.removeItem('demoOpened');
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        showPopupIfNeeded('visibilitychange');
      }
    };

    const handleFocus = () => {
      showPopupIfNeeded('focus');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    showPopupIfNeeded('mount');
    
    intervalId = setInterval(() => {
      if (!document.hidden) {
        const demoOpened = localStorage.getItem('demoOpened');
        if (demoOpened === 'true' && !showFeedbackPopup) {
          showPopupIfNeeded('interval');
        }
      }
    }, 2000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [showFeedbackPopup]);

  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit feedback');
      }
      
      if (window.posthog) {
        window.posthog.capture('feedback_submitted_success', {
          source_page: '/credit-score'
        });
      }
    } catch (error) {
      if (window.posthog) {
        window.posthog.capture('feedback_submission_failed', {
          error: error.message,
          source_page: '/credit-score'
        });
      }
    }
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    const submissionData = {
      email,
      country: country || null,
      priority_problem: priorityProblem === 'other' ? otherProblem : priorityProblem,
      source_page: '/credit-score',
      source_campaign: 'credit_score_protection',
      signup_url: window.location.href,
      referrer_url: document.referrer || null,
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_content: urlParams.get('utm_content') || null,
      timestamp: new Date().toISOString(),
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      setIsSubmitting(false);
      setWaitlistCount(prev => prev + 1);
      
      if (window.posthog) {
        window.posthog.capture('waitlist_submitted_credit_score', {
          source_page: '/credit-score',
          source_campaign: 'credit_score_protection',
          priority_problem: submissionData.priority_problem,
          has_utm: !!(urlParams.get('utm_source') || urlParams.get('utm_campaign'))
        });
      }
      
      // Redirect to survey
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/credit-score',
          source_campaign: 'credit_score_protection',
          priority_problem: submissionData.priority_problem
        }
      });
      
    } catch (error) {
      const pendingSubmissions = JSON.parse(localStorage.getItem('pendingWaitlist') || '[]');
      pendingSubmissions.push(submissionData);
      localStorage.setItem('pendingWaitlist', JSON.stringify(pendingSubmissions));
      
      setIsSubmitting(false);
      
      if (window.posthog) {
        window.posthog.capture('waitlist_joined_offline', {
          saved_to_backup: true,
          source_page: '/credit-score',
          priority_problem: submissionData.priority_problem,
        });
      }
      
      // Redirect to survey even for offline signups
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/credit-score',
          source_campaign: 'credit_score_protection',
          priority_problem: submissionData.priority_problem
        }
      });
    }
  };

  // Track page view
  useEffect(() => {
    if (window.posthog) {
      window.posthog.capture('page_view_credit_score', {
        page_path: '/credit-score',
        source_campaign: 'credit_score_protection'
      });
    }
  }, []);

  return (
    <div className="credit-score-page">
      {/* Hero Section */}
      <section className="credit-hero-section">
        <div className="credit-hero-container">
          <div className="credit-hero-content">
            <h1 className="credit-hero-title">
              Protect Your Credit Score Before Small Mistakes Become Expensive
            </h1>
            <p className="credit-hero-subtitle">
              MySentinel watches credit utilization, interest leakage, and card usage patterns 
              so you can act before they affect your credit score or cost you more in borrowing.
            </p>
            <p className="credit-hero-support">
              Know when one card is getting too high, when another card has unused room, 
              and what action may help reduce risk.
            </p>
            
            <div className="credit-hero-ctas">
              <Button 
                onClick={() => document.getElementById('credit-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
                className="credit-cta-primary"
              >
                Join Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
                variant="outline"
                className="credit-cta-secondary"
              >
                Explore Interactive Demo
              </Button>
            </div>
          </div>
          
          {/* Credit Alert Card Demo */}
          <div className="credit-alert-demo">
            <Card className="credit-alert-card">
              <CardContent className="credit-alert-content">
                <div className="alert-header">
                  <AlertCircle className="alert-icon-warning" />
                  <span className="alert-badge-credit">Credit Utilization Alert</span>
                </div>
                
                <div className="alert-body-credit">
                  <div className="credit-card-row">
                    <CreditCard className="card-icon" />
                    <div className="card-info">
                      <span className="card-name">Card A:</span>
                      <span className="card-util high">42% used</span>
                    </div>
                  </div>
                  <div className="credit-card-row">
                    <CreditCard className="card-icon" />
                    <div className="card-info">
                      <span className="card-name">Card B:</span>
                      <span className="card-util low">8% used</span>
                    </div>
                  </div>
                  
                  <div className="suggested-action">
                    <h4>Suggested action:</h4>
                    <p>Rebalance before statement close</p>
                  </div>
                  
                  <div className="potential-impact">
                    <h4>Potential impact:</h4>
                    <p>Reduce utilization risk and protect borrowing power</p>
                  </div>
                </div>
                
                <Button className="alert-action-btn-credit">
                  View Recommendation
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="credit-problem-section">
        <div className="section-container">
          <h2 className="section-title">Credit Score Damage Often Happens Quietly</h2>
          <p className="section-description-large">
            Many people think paying on time is enough. But credit card utilization can still affect 
            credit score health, especially when one card carries a high balance while another card has unused limit.
          </p>
          
          <div className="problem-grid">
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Utilization changes throughout the month</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Statement dates are easy to forget</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">One card can look risky even when total available credit is healthy</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Interest builds quietly when balances remain unpaid</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">The best card to use changes depending on balance, limit, rewards, and timing</p>
            </div>
          </div>
          
          <p className="credit-closing-line">
            <AlertCircle className="inline-icon-warning" />
            A small utilization mistake today can become a higher borrowing cost later.
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="credit-solution-section">
        <div className="section-container">
          <div className="solution-header">
            <Shield className="solution-icon-large" />
            <h2 className="section-title">A Proactive Credit Score Watchdog</h2>
          </div>
          
          <p className="section-description-large">
            MySentinel continuously watches for utilization risk, interest leakage, and card usage opportunities.
          </p>
          
          <p className="section-description">
            Instead of only showing your balances, it highlights the next best action before the issue becomes costly.
          </p>
          
          <div className="solution-actions-grid">
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <CreditCard className="solution-action-icon" />
                <h3 className="solution-action-title">Use a different card before one card gets too high</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <BarChart3 className="solution-action-icon" />
                <h3 className="solution-action-title">Rebalance spending across cards</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <Target className="solution-action-icon" />
                <h3 className="solution-action-title">Pay down the right balance before statement close</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <DollarSign className="solution-action-icon" />
                <h3 className="solution-action-title">Avoid unnecessary credit card interest</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <Shield className="solution-action-icon" />
                <h3 className="solution-action-title">Protect your credit score before applying for loans, mortgages, or refinancing</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Monitor Everything Section */}
      <section className="credit-monitor-everything-section">
        <div className="section-container">
          <h2 className="section-title">Monitor Everything. Not Just One Account.</h2>
          <p className="section-description-large">
            Financial leaks rarely exist in a single place.
          </p>
          
          <p className="monitor-intro-text">
            Most people have multiple:
          </p>
          
          <div className="multiple-items-grid">
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Bank accounts</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Credit cards</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Subscriptions</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Recurring bills</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Service providers</span>
            </div>
          </div>
          
          <p className="monitor-pattern-text">
            Reviewing them individually makes it easy to miss important patterns.
          </p>
          
          <p className="section-description-large">
            MySentinel is designed to securely monitor your connected financial accounts together and identify 
            opportunities, risks, and money leaks across your entire financial ecosystem.
          </p>
          
          <div className="monitor-cards-grid">
            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Activity className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bank Accounts</h3>
                <p className="monitor-card-description">
                  Monitor multiple connected accounts in one place.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <CreditCard className="monitor-card-icon" />
                <h3 className="monitor-card-title">Credit Cards</h3>
                <p className="monitor-card-description">
                  Track utilization, balances, and interest risks across cards.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Bell className="monitor-card-icon" />
                <h3 className="monitor-card-title">Subscriptions</h3>
                <p className="monitor-card-description">
                  Detect recurring charges and forgotten services.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <TrendingUp className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bills</h3>
                <p className="monitor-card-description">
                  Monitor telecom, insurance, utilities, and other recurring expenses.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="monitor-highlight-box">
            <Shield className="monitor-highlight-icon" />
            <p className="monitor-highlight-text">Connect once. Monitor everything.</p>
          </div>
          
          <div className="monitor-supporting-content">
            <p className="section-description-large">
              The most valuable financial insights often appear when information is viewed across multiple 
              accounts rather than in isolation.
            </p>
            
            <h3 className="monitor-examples-title">Examples:</h3>
            <div className="monitor-examples">
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>One card approaching high utilization while another remains underused</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Duplicate subscriptions across different payment methods</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Rising recurring costs spread across multiple providers</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Hidden money leaks that are difficult to see when reviewing accounts individually</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Balance Rebalancing Section */}
      <section className="credit-rebalancing-section">
        <div className="section-container">
          <h2 className="section-title">Balance Rebalancing Alerts</h2>
          <p className="section-description">
            If one credit card exceeds recommended utilization while another card has unused room, 
            MySentinel alerts you and suggests how to rebalance.
          </p>
          
          <div className="rebalancing-demo">
            <Card className="rebalancing-card">
              <CardContent className="rebalancing-content">
                <div className="rebalancing-header">
                  <TrendingUp className="rebalancing-icon" />
                  <h3>Balance Rebalancing Opportunity</h3>
                </div>
                
                <div className="rebalancing-status">
                  <p>Card A is at <strong className="util-high">42% utilization</strong></p>
                  <p>Card B is at <strong className="util-low">8% utilization</strong></p>
                </div>
                
                <div className="rebalancing-suggestion">
                  <h4>Suggested action:</h4>
                  <p>Move upcoming spending to Card B or reduce Card A before statement close.</p>
                </div>
                
                <div className="rebalancing-why">
                  <h4>Why it matters:</h4>
                  <p>This may help reduce utilization risk and protect your credit score profile.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Smart Card Guidance Section */}
      <section className="credit-card-guidance-section">
        <div className="section-container">
          <h2 className="section-title">Which Card Should I Use?</h2>
          <p className="section-description-large">
            Choosing the wrong card can create unnecessary interest, missed rewards, or utilization pressure.
          </p>
          
          <p className="section-description">
            MySentinel helps guide everyday spending decisions by showing which card may be better to use based on:
          </p>
          
          <div className="guidance-factors-grid">
            <div className="guidance-factor">
              <BarChart3 className="factor-icon" />
              <span>Current utilization</span>
            </div>
            <div className="guidance-factor">
              <Target className="factor-icon" />
              <span>Available limit</span>
            </div>
            <div className="guidance-factor">
              <Bell className="factor-icon" />
              <span>Upcoming statement dates</span>
            </div>
            <div className="guidance-factor">
              <DollarSign className="factor-icon" />
              <span>Interest risk</span>
            </div>
            <div className="guidance-factor">
              <Zap className="factor-icon" />
              <span>Possible rewards or cashback</span>
            </div>
          </div>
          
          <div className="guidance-example">
            <Card className="guidance-card">
              <CardContent className="guidance-card-content">
                <div className="guidance-recommendation">
                  <CheckCircle2 className="rec-icon" />
                  <h3>Use Card B for groceries this week.</h3>
                </div>
                
                <div className="guidance-reason">
                  <h4>Reason:</h4>
                  <p>Card A is approaching high utilization, while Card B has more available room.</p>
                </div>
                
                <div className="guidance-impact">
                  <h4>Potential impact:</h4>
                  <p>Reduce utilization pressure and avoid unnecessary interest risk.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Interest Leakage Section */}
      <section className="credit-interest-section">
        <div className="section-container">
          <h2 className="section-title">Reduce Unnecessary Credit Card Interest</h2>
          <p className="section-description-large">
            Credit card interest can build quietly when balances remain unpaid or payments are not prioritized effectively.
          </p>
          
          <p className="section-description">
            MySentinel helps identify:
          </p>
          
          <div className="interest-features-grid">
            <div className="interest-feature">
              <AlertCircle className="interest-icon" />
              <span>Cards carrying interest risk</span>
            </div>
            <div className="interest-feature">
              <Target className="interest-icon" />
              <span>Balances that should be prioritized</span>
            </div>
            <div className="interest-feature">
              <Bell className="interest-icon" />
              <span>Payment timing opportunities</span>
            </div>
            <div className="interest-feature">
              <Activity className="interest-icon" />
              <span>Spending patterns that create avoidable interest</span>
            </div>
          </div>
          
          <div className="interest-example">
            <Card className="interest-card">
              <CardContent className="interest-card-content">
                <div className="interest-alert-header">
                  <DollarSign className="interest-alert-icon" />
                  <h3>Interest Leakage Detected</h3>
                </div>
                
                <div className="interest-details">
                  <p>Card A carried a balance for 2 months</p>
                  <p className="interest-cost">Estimated interest risk: <strong>$48 this cycle</strong></p>
                </div>
                
                <div className="interest-action">
                  <h4>Suggested action:</h4>
                  <p>Prioritize Card A before adding new spending elsewhere.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Long-Term Financial Impact Section - Enhanced */}
      <section className="credit-long-term-impact-section">
        <div className="section-container">
          <div className="impact-intro">
            <h2 className="section-title">A Small Credit Difference Can Become a Big Cost</h2>
            <p className="section-description-large">
              Many people focus on their current credit score but underestimate how credit profile changes 
              can affect future borrowing costs.
            </p>
            <p className="section-description">
              Even a small difference in borrowing rates can add up significantly over the life of a mortgage, 
              auto loan, or refinancing decision.
            </p>
          </div>

          {/* Visual Comparison Card */}
          <div className="borrowing-comparison-container">
            <div className="comparison-header">
              <h3 className="comparison-title">Illustrative Example</h3>
              <span className="comparison-badge">For illustration purposes only</span>
            </div>

            <div className="comparison-details-grid">
              <div className="comparison-detail">
                <span className="detail-label">Mortgage Amount</span>
                <span className="detail-value">£300,000</span>
              </div>
              <div className="comparison-detail">
                <span className="detail-label">Term</span>
                <span className="detail-value">25 Years</span>
              </div>
            </div>

            <div className="rate-scenarios-grid">
              <Card className="rate-scenario-card scenario-a">
                <CardContent className="rate-scenario-content">
                  <div className="scenario-header">
                    <span className="scenario-label">Rate Scenario A</span>
                  </div>
                  <div className="scenario-rate">
                    <span className="rate-value">4.0%</span>
                    <span className="rate-label">Interest Rate</span>
                  </div>
                  <div className="scenario-footer">
                    <CheckCircle2 className="scenario-icon" />
                    <span className="scenario-status">Better rate profile</span>
                  </div>
                </CardContent>
              </Card>

              <div className="comparison-arrow">
                <ArrowRight className="arrow-icon" />
                <span className="arrow-label">vs</span>
              </div>

              <Card className="rate-scenario-card scenario-b">
                <CardContent className="rate-scenario-content">
                  <div className="scenario-header">
                    <span className="scenario-label">Rate Scenario B</span>
                  </div>
                  <div className="scenario-rate">
                    <span className="rate-value">4.5%</span>
                    <span className="rate-label">Interest Rate</span>
                  </div>
                  <div className="scenario-footer">
                    <AlertCircle className="scenario-icon warning" />
                    <span className="scenario-status">Higher rate profile</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="potential-impact-box">
              <div className="impact-icon-wrapper">
                <TrendingUp className="impact-icon" />
              </div>
              <div className="impact-content">
                <h4 className="impact-title">Potential Additional Interest</h4>
                <p className="impact-amount">£20,000–£30,000+</p>
                <p className="impact-disclaimer">Over the life of the loan (illustrative example only)</p>
              </div>
            </div>
          </div>

          {/* Key Message */}
          <div className="impact-key-message">
            <div className="message-icon-wrapper">
              <Shield className="message-icon" />
            </div>
            <p className="message-text">
              Small credit decisions today can influence borrowing costs for years to come.
            </p>
          </div>

          {/* Supporting Content */}
          <div className="impact-supporting-content">
            <Card className="supporting-card">
              <CardContent className="supporting-card-content">
                <div className="supporting-point">
                  <BarChart3 className="supporting-icon" />
                  <div className="supporting-text">
                    <h4>Credit Profile Factors</h4>
                    <p>
                      Credit utilization is one of several factors lenders may consider when assessing risk.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="supporting-card">
              <CardContent className="supporting-card-content">
                <div className="supporting-point">
                  <Target className="supporting-icon" />
                  <div className="supporting-text">
                    <h4>Financial Flexibility</h4>
                    <p>
                      Maintaining a healthy credit profile may help improve future financial flexibility 
                      and reduce unnecessary borrowing costs.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Visual Flow Diagram */}
          <div className="credit-flow-diagram">
            <h3 className="flow-title">Why Proactive Monitoring Matters</h3>
            <div className="flow-steps">
              <div className="flow-step">
                <div className="flow-icon-wrapper">
                  <Activity className="flow-icon" />
                </div>
                <h4 className="flow-step-title">Credit Utilization</h4>
                <p className="flow-step-desc">Monitor card balances</p>
              </div>

              <div className="flow-connector">
                <ArrowRight className="connector-arrow" />
              </div>

              <div className="flow-step">
                <div className="flow-icon-wrapper">
                  <BarChart3 className="flow-icon" />
                </div>
                <h4 className="flow-step-title">Credit Profile</h4>
                <p className="flow-step-desc">Maintain healthy usage</p>
              </div>

              <div className="flow-connector">
                <ArrowRight className="connector-arrow" />
              </div>

              <div className="flow-step">
                <div className="flow-icon-wrapper">
                  <DollarSign className="flow-icon" />
                </div>
                <h4 className="flow-step-title">Borrowing Cost</h4>
                <p className="flow-step-desc">Potential rate impact</p>
              </div>

              <div className="flow-connector">
                <ArrowRight className="connector-arrow" />
              </div>

              <div className="flow-step">
                <div className="flow-icon-wrapper highlight">
                  <TrendingUp className="flow-icon" />
                </div>
                <h4 className="flow-step-title">Real Financial Impact</h4>
                <p className="flow-step-desc">Long-term savings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="credit-waitlist" className="credit-waitlist-section">
        <div className="section-container">
          <div className="waitlist-header">
            <h2 className="section-title">Join the Waitlist</h2>
            <p className="waitlist-count">{waitlistCount.toLocaleString()} people waiting</p>
          </div>
          
          <div className="waitlist-form-container">
            {submitSuccess ? (
              <div className="success-message">
                <CheckCircle2 className="success-icon" />
                <h3>Thanks for joining!</h3>
                <p>You&apos;re on the list to help us build proactive credit score protection.</p>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                <div className="form-group">
                  <label htmlFor="credit-email" className="form-label">Email *</label>
                  <Input
                    id="credit-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="credit-country" className="form-label">Country (optional)</label>
                  <Input
                    id="credit-country"
                    type="text"
                    placeholder="e.g., United States"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group priority-problem-group">
                  <label className="form-label form-label-required">
                    What would make you sign up today? *
                  </label>
                  <p className="form-sublabel">
                    Select the one problem you would most like solved automatically.
                  </p>
                  <div className="radio-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="trial_reminder"
                        checked={priorityProblem === 'trial_reminder'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Never forget a free trial again</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="bill_increase"
                        checked={priorityProblem === 'bill_increase'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Catch bill increases automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="credit_protection"
                        checked={priorityProblem === 'credit_protection'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Protect my credit score automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="reduce_interest"
                        checked={priorityProblem === 'reduce_interest'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Reduce unnecessary credit card interest</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="find_subscriptions"
                        checked={priorityProblem === 'find_subscriptions'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Find hidden subscriptions and recurring charges</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="continuous_monitoring"
                        checked={priorityProblem === 'continuous_monitoring'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Continuously monitor my finances for money leaks</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="other"
                        checked={priorityProblem === 'other'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Other</span>
                    </label>
                    
                    {priorityProblem === 'other' && (
                      <div className="other-problem-input">
                        <Input
                          type="text"
                          placeholder="Please specify"
                          value={otherProblem}
                          onChange={(e) => setOtherProblem(e.target.value)}
                          required
                          className="form-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="submit-btn"
                  size="lg"
                >
                  {isSubmitting ? 'Joining...' : 'Join Early Access'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="credit-final-cta-section">
        <div className="section-container">
          <h2 className="final-cta-title">
            Protect Your Credit Score Before Small Mistakes Become Expensive
          </h2>
          <p className="final-cta-subtitle">
            Join early access and get alerted before credit card utilization impacts your score 
            or costs you more in borrowing.
          </p>
          <div className="final-cta-buttons">
            <Button 
              onClick={() => document.getElementById('credit-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              className="final-cta-btn"
              size="lg"
            >
              Join Early Access
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
              variant="outline"
              className="final-demo-btn"
              size="lg"
            >
              Try Interactive Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Feedback Popup */}
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default CreditScorePage;
